/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api\openvpnApi.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the openvpn module.
────────────────────────────────────────────────────────────────*/


import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type {
    OpenVPNConfig,
    OpenVPNConfigFormState,
    OpenVPNClient,
    CreateClientRequest
} from '../types';

export interface OpenVPNConfigResponse {
    success: boolean;
    data: OpenVPNConfig;
    message?: string;
}

export interface OpenVPNClientsResponse {
    success: boolean;
    data: OpenVPNClient[];
    message?: string;
}

export interface OpenVPNClientResponse {
    success: boolean;
    data: OpenVPNClient;
    message?: string;
}

export interface DownloadResponse {
    success: boolean;
    data: {
        filename: string;
        content: string;
        mimeType: string;
    };
    message?: string;
}

/**
 * Get OpenVPN configuration
 */
export async function getOpenVPNConfig(
    tenantSlug?: string,
    userId?: string
): Promise<OpenVPNConfigResponse> {
    const response = await fetch(buildApiUrl('/api/network/openvpn/config'), {
        headers: getApiHeaders(tenantSlug, userId)
    });

    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
    }

    return await response.json();
}

/**
 * Update OpenVPN configuration
 */
export async function updateOpenVPNConfig(
    data: Partial<OpenVPNConfigFormState>,
    tenantSlug?: string,
    userId?: string
): Promise<OpenVPNConfigResponse> {
    const response = await fetch(buildApiUrl('/api/network/openvpn/config'), {
        method: 'PUT',
        headers: {
            ...getApiHeaders(tenantSlug, userId),
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP ${response.status}`);
    }

    return await response.json();
}

/**
 * Get all OpenVPN clients
 */
export async function getOpenVPNClients(
    tenantSlug?: string,
    userId?: string
): Promise<OpenVPNClientsResponse> {
    const response = await fetch(buildApiUrl('/api/network/openvpn/clients'), {
        headers: getApiHeaders(tenantSlug, userId)
    });

    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
    }

    return await response.json();
}

/**
 * Create a new OpenVPN client
 */
export async function createOpenVPNClient(
    data: CreateClientRequest,
    tenantSlug?: string,
    userId?: string
): Promise<OpenVPNClientResponse> {
    const response = await fetch(buildApiUrl('/api/network/openvpn/clients'), {
        method: 'POST',
        headers: {
            ...getApiHeaders(tenantSlug, userId),
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP ${response.status}`);
    }

    return await response.json();
}

/**
 * Delete an OpenVPN client
 */
export async function deleteOpenVPNClient(
    clientId: number,
    tenantSlug?: string,
    userId?: string
): Promise<{ success: boolean; message?: string }> {
    const response = await fetch(buildApiUrl(`/api/network/openvpn/clients/${clientId}`), {
        method: 'DELETE',
        headers: getApiHeaders(tenantSlug, userId)
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP ${response.status}`);
    }

    return await response.json();
}

/**
 * Revoke an OpenVPN client certificate
 */
export async function revokeOpenVPNClient(
    clientId: number,
    tenantSlug?: string,
    userId?: string
): Promise<OpenVPNClientResponse> {
    const response = await fetch(buildApiUrl(`/api/network/openvpn/clients/${clientId}/revoke`), {
        method: 'POST',
        headers: {
            ...getApiHeaders(tenantSlug, userId),
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP ${response.status}`);
    }

    return await response.json();
}

/**
 * Download client configuration file
 * Handles both binary (ZIP, TAR) and text (OVPN) files
 */
export async function downloadClientConfig(
    clientId: number,
    tenantSlug?: string,
    userId?: string
): Promise<void> {
    const response = await fetch(buildApiUrl(`/api/network/openvpn/clients/${clientId}/download`), {
        headers: getApiHeaders(tenantSlug, userId)
    });

    if (!response.ok) {
        // Try to parse error as JSON
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || `HTTP ${response.status}`);
        }
        throw new Error(`HTTP ${response.status}`);
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'client_config';
    if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (match) {
            filename = match[1];
        }
    }

    // Download the file as blob
    const blob = await response.blob();

    // Create download link and trigger download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    // Cleanup
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
}

/**
 * Refresh client connection status
 */
export async function refreshClientStatus(
    tenantSlug?: string,
    userId?: string
): Promise<OpenVPNClientsResponse> {
    const response = await fetch(buildApiUrl('/api/network/openvpn/clients/status'), {
        method: 'POST',
        headers: {
            ...getApiHeaders(tenantSlug, userId),
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP ${response.status}`);
    }

    return await response.json();
}
